import { ENVIRONMENT_WEBSOCKET_TICKET_PARAM } from "@afar-research/contracts";
import * as Context from "effect/Context";
import * as Effect from "effect/Effect";
import * as Layer from "effect/Layer";
import type * as Scope from "effect/Scope";

import type { HostEnvironmentEntry } from "./host.ts";
import {
  type ConnectionAttemptError,
  type ConnectionAttemptStage,
  type ConnectionTarget,
  PairedConnectionTarget,
  type PreparedConnection,
} from "./model.ts";
import * as ConnectionResolver from "./resolver.ts";
import * as RpcSession from "../rpc/session.ts";

export type ConnectionDriverProgress =
  | {
      readonly stage: "preparing";
    }
  | {
      readonly stage: Exclude<ConnectionAttemptStage, "preparing">;
      readonly prepared: PreparedConnection;
    };

export interface EnvironmentConnectionLease {
  readonly prepared: PreparedConnection;
  readonly session: RpcSession.RpcSession;
}

export class ConnectionDriver extends Context.Service<
  ConnectionDriver,
  {
    readonly connect: (
      entry: HostEnvironmentEntry,
      reportProgress: (progress: ConnectionDriverProgress) => Effect.Effect<void>,
    ) => Effect.Effect<EnvironmentConnectionLease, ConnectionAttemptError, Scope.Scope>;
  }
>()("@afar-research/client-runtime/connection/driver/ConnectionDriver") {}

/** The socket URL with its one-use ticket removed; a spent ticket is still not state to show. */
function observableSocketUrl(socketUrl: string): string {
  const url = new URL(socketUrl);
  url.searchParams.delete(ENVIRONMENT_WEBSOCKET_TICKET_PARAM);
  return url.toString();
}

/**
 * A paired target with its session emptied: the connection is already open with it, and the
 * registry's own entry keeps it for the next one.
 */
function observableTarget(target: ConnectionTarget): ConnectionTarget {
  if (target._tag !== "PairedConnectionTarget") return target;
  const { _tag, ...fields } = target;
  return new PairedConnectionTarget({ ...fields, session: "" });
}

/**
 * What views see of a connection, carrying no credential: the subprotocol a host process
 * authorizes with, the ticket a client opened its socket with, and a paired session all stay
 * here. The registry's entries still hold a paired session, because reconnecting needs it. `authorizeSocket` travels: it mints a fresh one-use ticket on each call without
 * revealing the credential behind it.
 */
function observableConnection(prepared: PreparedConnection): PreparedConnection {
  const target = observableTarget(prepared.target);
  return {
    environmentId: prepared.environmentId,
    label: prepared.label,
    httpBaseUrl: prepared.httpBaseUrl,
    socketUrl: observableSocketUrl(prepared.socketUrl),
    target,
    ...(prepared.authorizeSocket ? { authorizeSocket: prepared.authorizeSocket } : {}),
  };
}

export const make = Effect.gen(function* () {
  const resolver = yield* ConnectionResolver.ConnectionResolver;
  const sessions = yield* RpcSession.RpcSessionFactory;

  const connect = Effect.fn("ConnectionDriver.connect")(function* (
    entry: HostEnvironmentEntry,
    reportProgress: (progress: ConnectionDriverProgress) => Effect.Effect<void>,
  ) {
    const target = entry.target;
    yield* Effect.annotateCurrentSpan({
      "connection.environment.id": target.environmentId,
      "connection.target.kind": target._tag,
    });
    yield* reportProgress({ stage: "preparing" });
    const prepared = yield* resolver.prepare(entry);
    const observablePrepared = observableConnection(prepared);
    yield* reportProgress({ stage: "opening", prepared: observablePrepared });
    const session = yield* sessions.connect(prepared);
    yield* reportProgress({ stage: "synchronizing", prepared: observablePrepared });
    yield* session.ready;
    return { prepared: observablePrepared, session } satisfies EnvironmentConnectionLease;
  });

  return ConnectionDriver.of({ connect });
});

export const layer = Layer.effect(ConnectionDriver, make);
